import {getProfileName} from '@/utils/getProfileName';
import {authMiddleware} from '@/helpers/authMiddleware';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {prismaClient} from '@/helpers/prismaClient.server';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {z} from 'zod';

/**
 * Follows a public event list for the authenticated user.
 * Throws "List not found" if the list doesn't exist or is private.
 * Throws if the user tries to follow their own list.
 * Idempotent — following an already-followed list is a no-op.
 */
export const followEventList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid list ID');
    return result.data;
  })
  .handler(async ({data: eventListId, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: eventListId},
    });

    if (!list || list.visibility !== 'PUBLIC') {
      throw new Error('List not found');
    }

    if (list.profileId === profile.id) {
      throw new Error('Cannot follow your own list');
    }

    await db.listFollower.upsert({
      where: {
        profileId_eventListId: {
          profileId: profile.id,
          eventListId,
        },
      },
      create: {profileId: profile.id, eventListId},
      update: {},
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'List Followed',
      {list_id: eventListId},
      requestContext
    );

    return {success: true};
  });

/**
 * Unfollows an event list for the authenticated user.
 * No-op if the user is not following the list.
 */
export const unfollowEventList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid list ID');
    return result.data;
  })
  .handler(async ({data: eventListId, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    await db.listFollower.deleteMany({
      where: {profileId: profile.id, eventListId},
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'List Unfollowed',
      {list_id: eventListId},
      requestContext
    );

    return {success: true};
  });

/** Shape returned by getFollowedLists for each followed list. */
interface FollowedListSummary {
  id: string;
  listId: string;
  listName: string;
  creatorName: string;
  creatorProfileId: string;
  eventCount: number;
}

/**
 * Returns all lists the authenticated user is following,
 * with creator info and event count.
 */
export const getFollowedLists = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .handler(async ({context}): Promise<FollowedListSummary[]> => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const follows = await db.listFollower.findMany({
      where: {profileId: profile.id},
      orderBy: {createdAt: 'desc'},
      include: {
        eventList: {
          include: {
            profile: {
              select: {
                id: true,
                displayName: true,
                firstName: true,
                lastName: true,
              },
            },
            _count: {select: {items: true}},
          },
        },
      },
    });

    return follows.map((f) => ({
      id: f.id,
      listId: f.eventList.id,
      listName: f.eventList.name,
      creatorName: getProfileName(f.eventList.profile),
      creatorProfileId: f.eventList.profile.id,
      eventCount: f.eventList._count.items,
    }));
  });
